import { ICanvasScaler } from "../../../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../../../shared/async/PromiseCompletionSource";
import { FadeScreen } from "../../../../game/components/FadeScreen";
import { PushResourcePackage } from "../../resources/PushResourcePackage";
import { IDestroyOptions, Point, Sprite, Text, TextStyle } from "pixi.js";
import { GameFont } from "../../../../common/GameFont";
import { CommonResourcesPackage } from "../../../../common/resources/CommonResourcesPackage";
import { BasePageView } from "../../../../common/components/BasePageView";
import { PushHintContainer } from "../../components/PushHintContainer";
import { SecondaryRewardContainer } from "../../components/SecondaryRewardContainer";
import { IPushController } from "../../controllers/IPushController";
import { TopPanel } from "../../../../game/components/toppanel/TopPanel";
import { WinCoinsAnimation } from "../../../../common/components/animations/WinCoinsAnimation";
import { gsap } from "gsap";
import { IAnalyticProviderWrapper } from "../../../../../../analytics/IAnalyticProviderWrapper";
import { VirtualScreenEvent } from "../../../../../../analytics/events/VirtualScreenEvent";
import { PushDialogData } from "../../../../game/flow/impl/dialogAction/PushDialogData";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";

export class SecondaryPage extends BasePageView {
    private readonly pushResources: PushResourcePackage;
    private readonly commonResources: CommonResourcesPackage;
    private readonly operationControls: PromiseCompletionSource<void>;
    private readonly pushController: IPushController;
    private readonly analyticProvider: IAnalyticProviderWrapper;
    private readonly dialogData: PushDialogData;
    private readonly logger: ILogger;
    private background: Sprite;
    private background2: Sprite;
    private headerText: Text;
    private hintContainer: PushHintContainer;
    private secondaryContainer: SecondaryRewardContainer;
    private topPanel: TopPanel;
    private coinsCollectPoint: Point;
    private coinsCollectAnimation: WinCoinsAnimation;

    public constructor(operationControls: PromiseCompletionSource<void>,
        canvasScaler: ICanvasScaler,
        pushResources: PushResourcePackage,
        commonResources: CommonResourcesPackage,
        pushController: IPushController,
        analyticProvider: IAnalyticProviderWrapper,
        topPanel: TopPanel,
        dialogData: PushDialogData,
        logger: ILogger
    ) {
        super(canvasScaler);
        this.pushResources = pushResources;
        this.commonResources = commonResources;
        this.operationControls = operationControls;
        this.pushController = pushController;
        this.analyticProvider = analyticProvider;
        this.topPanel = topPanel;
        this.dialogData = dialogData;
        this.logger = logger;
        this.build();
        this.once("added", this.show.bind(this));
    }

    protected override build(): void {
        this.background = new Sprite(this.pushResources.assets_views_push_coins_bg_png);
        this.background.anchor.set(0.5, 1);
        this.background.scale.set(1.2);
        this.addChild(this.background);

        this.background2 = new Sprite(this.pushResources.assets_views_push_coins_bg2_png);
        this.background2.anchor.set(0.5, 0);
        this.background2.scale.set(1.20);
        this.addChild(this.background2);

        this.headerText = new Text('GET UPDATES\nABOUT FREE BONUSES\nIN THE GAME', new HeaderTextStyle());
        this.headerText.anchor.set(0.5);
        this.headerText.skew.set(0, -0.09);
        this.addChild(this.headerText);

        this.hintContainer = new PushHintContainer(this.commonResources);
        this.addChild(this.hintContainer);

        this.secondaryContainer = new SecondaryRewardContainer(this.dialogData.allowPushNotificationsRewardInCoins, this.pushResources, this.commonResources);
        this.addChild(this.secondaryContainer);

        this.topPanel.bringToFront();
        this.coinsCollectPoint = new Point();
        super.build();
    }

    private async show(): Promise<void> {
        try {
            if ((window as any).hasOwnProperty('_askForPushNotifications')) {
                const result = await (window as any)._askForPushNotifications();
                if ((window as any).Notification.permission == 'granted') {
                    this.analyticProvider.registerGoogleEvent(new VirtualScreenEvent("Accept Push notifications", this.analyticProvider.currentEventIndex));
                    if (result.hasOwnProperty("timeout")) {
                        // We haven't received answer from push server for subscription and resolved by timeout
                        this.logger.error("PushManager.subscribe method timeout.");
                        // Handle special server method to still grant coins for user
                        await this.pushController.allowPushNotification(false, undefined, true);
                    } else {
                        await this.pushController.allowPushNotification(false, result, false);
                    }
                    this.coinsCollectAnimation = new WinCoinsAnimation(this.commonResources, this.commonResources.assets_common_ui_top_panel_coin_png, this.coinsCollectPoint, this.dialogData.allowPushNotificationsRewardInCoins);
                    this.coinsCollectAnimation.position.set(this.canvasScaler.referenceWidth * 0.45, this.canvasScaler.referenceHeight * 0.45);
                    this.addChild(this.coinsCollectAnimation);
                    await this.coinsCollectAnimation.startAnimation();
                } else {
                    await this.pushController.allowPushNotification(false);
                    this.analyticProvider.registerGoogleEvent(new VirtualScreenEvent("Decline Push notifications", this.analyticProvider.currentEventIndex));
                }
            }
        }
        catch (error: unknown) {
            this.logger.error("Unable to load popup", error);
        }
        await this.hidePage();
    }

    private async hidePage(): Promise<void> {
        const animationTimeLine = new gsap.core.Timeline();
        await animationTimeLine.to(this, { alpha: 0, duration: 0.5, ease: "power1.out" });
        this.operationControls.complete();
    }

    protected override layout(width: number, height: number): void {
        this.background.position.set(width * 0.5, 1892);
        this.background2.position.set(width * 0.5, -60);
        this.secondaryContainer.position.set(width * 0.5, 1411);
        this.hintContainer.position.set(308, 1166);
        this.headerText.position.set(width * 0.5, 390);
        this.coinsCollectPoint.set(0, TopPanel.DEFAULT_POSITION_Y - height * 0.45);
        super.layout(width, height);
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        this.topPanel.bringToOriginParent();
        super.destroy(options);
    }
}

class HeaderTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: [
                "#2896fe",
                "#4adef5"
            ],
            fillGradientType: 1,
            fillGradientStops: [
                0.1
            ],
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 96,
            strokeThickness: 1,
            letterSpacing: 0.8,
            lineHeight: 88
        }));
    }
}
